import React, { useState, useEffect } from 'react';
import { Swap, ASCSortIcon, DESCSortIcon } from '../../assets/icons';
import { getLocalStorageItem } from '../../utils';
import { LOCAL_STORAGE } from '../../constants';

const SORTING = {
    DEFAULT: 'DEFAULT',
    ASCENDING: 'ASC',
    DESCENDING: 'DESC',
};


export const RightSplitTable = ({
    fullProductData = [],
    rightTableLoading,
    setUpdatedMoves,
    rightTableColumn,
    tooltip
}) => {
    const [data, setData] = useState([]);
    const [sortInfo, setSortInfo] = useState({
        index: null,
        sortType: SORTING.DEFAULT,
        valueType: null,
    });
    const [editedQuantities, setEditedQuantities] = useState({});

    useEffect(() => {
        if (!Array.isArray(fullProductData)) return;

        const updatedEdits = {};
        const updatedData = [];

        fullProductData.forEach(item => {
            updatedEdits[item.move_id] = editedQuantities[item.move_id] || {
                producing_quantity: item.draft_produced_qty,
                damaged_quantity: item.draft_damaged_qty,
            };
            updatedData.push(item);
        });

        setEditedQuantities(updatedEdits);
        setData(sortData(updatedData, sortInfo));
    }, [fullProductData]);

    useEffect(() => {
        if (!Array.isArray(fullProductData)) return;
        const sorted = sortData(fullProductData, sortInfo);
        setData(sorted);
    }, [sortInfo]);

    const sortData = (unsortedData, sortInfo) => {
        const { sortType, index, valueType } = sortInfo;

        if (sortType === 'DEFAULT' || index === null) return unsortedData;

        const col = rightTableColumn[index];
        const dataFieldName = Array.isArray(col.dataField) ? col.dataField : [col.dataField];

        const getValue = (row) => {
            if (dataFieldName.length === 1) {
                let rawVal = row[dataFieldName[0]];

                if (col.name === 'Ready Status') {
                    const statusOrder = {
                        'Fully Ready': 1,
                        'Partially Ready': 2,
                        'Not Ready': 3,
                    };
                    return statusOrder[rawVal] ?? 999;
                }

                if (col.name === 'Lot No') {
                    const stringVal = String(rawVal || '');
                    const match = stringVal.match(/\d+/);
                    return match ? parseInt(match[0], 10) : 0;
                }

                return rawVal;
            }

            return dataFieldName.reduce((sum, key) => sum + (Number(row[key]) || 0), 0);
        };

        const sorted = [...unsortedData].sort((a, b) => {
            const aVal = getValue(a);
            const bVal = getValue(b);

            // If Ready Status (numbers now), no need to treat as string
            if (col.name === 'Ready Status') {
                return sortType === 'ASC' ? aVal - bVal : bVal - aVal;
            }

            if (valueType === 'String') {
                return sortType === 'ASC'
                    ? String(aVal).localeCompare(bVal)
                    : String(bVal).localeCompare(aVal);
            }

            const aNum = parseFloat(aVal) || 0;
            const bNum = parseFloat(bVal) || 0;
            return sortType === 'ASC' ? aNum - bNum : bNum - aNum;
        });

        return sorted;
    };


    const handleClick = (index, valueType) => {
        setSortInfo((prev) => {
            let nextSort;
            if (prev.index !== index || prev.sortType === SORTING.DESCENDING) {
                nextSort = SORTING.ASCENDING;
            } else if (prev.sortType === SORTING.ASCENDING) {
                nextSort = SORTING.DESCENDING;
            } else {
                nextSort = SORTING.ASCENDING;
            }

            return { index, sortType: nextSort, valueType };
        });
    };


    return (
        <div className={`table-container table-1 position-relative mb-3 d-none table-right-split`}>
            <table className="w-100">
                <thead className="mb-4">
                    <tr>
                        {rightTableColumn.map((col, index) => (
                            <th key={index} className={col.classNameTH}>
                                <div className="text-btn-div d-flex align-items-center justify-content-start">
                                    <span>{col.name}</span>
                                    {col.sort ? (
                                        sortInfo.index === index && sortInfo.sortType === SORTING.DEFAULT ? (
                                            <Swap handleClick={() => handleClick(index, col.valueType)} />
                                        ) : sortInfo.index === index && sortInfo.sortType === SORTING.ASCENDING ? (
                                            <ASCSortIcon handleClick={() => handleClick(index, col.valueType)} />
                                        ) : (
                                            <DESCSortIcon handleClick={() => handleClick(index, col.valueType)} />
                                        )
                                    ) : null}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, rowIndex) => (
                        <tr key={rowIndex} className="table-row row-populate" style={{ cursor: 'default' }}>
                            {rightTableColumn.map((column, colIndex) => {
                                const fieldKeys = Array.isArray(column.dataField)
                                    ? column.dataField
                                    : [column.dataField];
                                const isProducing = column.classNameTD?.includes('producing-quantity-td') && !column.name.includes('Damaged');
                                const isDamaged = column.classNameTD?.includes('producing-quantity-td') && column.name.includes('Damaged');

                                if (column.isCustom && typeof column.customFormatter === 'function') {
                                    const fieldData = fieldKeys.reduce((acc, key) => {
                                        acc[key] = row[key];
                                        return acc;
                                    }, {});
                                    return (
                                        <td key={colIndex} className={column.classNameTD}>
                                            {column.customFormatter({
                                                ...fieldData,
                                                row,
                                                rowIndex,
                                                setUpdatedMoves,
                                                setEditedQuantities,
                                            })}
                                        </td>
                                    );
                                }

                                const value = (() => {
                                    const moveId = row.move_id;
                                    const editData = editedQuantities[moveId] || {};
                                    if (isProducing) return editData.producing_quantity ?? '';
                                    if (isDamaged) return editData.damaged_quantity ?? '';
                                    return row[fieldKeys[0]];
                                })();

                                if (isProducing || isDamaged) {
                                    return (
                                        <td key={colIndex} className={column.classNameTD}>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={value}
                                                min={0}
                                                disabled={getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' && row.ready_status === 'Not Ready'}
                                                onChange={(e) => {
                                                    const moveId = row.move_id;
                                                    const updatedValue = e.target.value;

                                                    setEditedQuantities(prev => ({
                                                        ...prev,
                                                        [moveId]: {
                                                            ...prev[moveId],
                                                            [isProducing ? 'producing_quantity' : 'damaged_quantity']: updatedValue,
                                                        },
                                                    }));

                                                    setUpdatedMoves(prev => ({
                                                        ...prev,
                                                        [moveId]: {
                                                            producing_quantity: isProducing
                                                                ? updatedValue
                                                                : (prev[moveId]?.producing_quantity ?? row.draft_produced_qty),
                                                            damaged_quantity: isDamaged
                                                                ? updatedValue
                                                                : (prev[moveId]?.damaged_quantity ?? row.draft_damaged_qty),
                                                        },
                                                    }));
                                                }}
                                                onWheel={(e) => e.target.blur()}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-' || e.key === 'e') e.preventDefault();
                                                }}
                                                style={{ width: '80px' }}
                                            />
                                        </td>
                                    );
                                }
                                return (
                                    <td
                                        key={colIndex}
                                        className={column.classNameTD}
                                        title={
                                            tooltip && typeof value === 'string' && value.length > 50
                                                ? value
                                                : undefined
                                        }
                                    >
                                        {typeof value === 'string' && value.length > 50 ? `${value.slice(0, 50)}...` : value}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>

                {/* <a href='' className='move_arrow d-none'>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><g data-name="90-Arrow Up"><path d="M16 0a16 16 0 1 0 16 16A16 16 0 0 0 16 0zm0 30a14 14 0 1 1 14-14 14 14 0 0 1-14 14z" /><path d="m15.29 10.29-8 8L8.7 19.7l7.3-7.29 7.29 7.29 1.41-1.41-8-8a1 1 0 0 0-1.41 0z" /></g></svg>
                </a> */}
            </table>
            {rightTableLoading && (
                <div className="text-center my-2">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    <span className="ms-2">Loading...</span>
                </div>
            )}
        </div>
    )
}